import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { categories } from "@/mocks/categories";

const languages = [
  { code: "en", label: "languageEnglish" },
  { code: "fr", label: "languageFrench" },
  { code: "da", label: "languageDanish" },
  { code: "de", label: "languageGerman" },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [catDropdownOpen, setCatDropdownOpen] = useState(false);
  const [langDropdownOpen, setLangDropdownOpen] = useState(false);
  const catRef = useRef<HTMLDivElement>(null);
  const langRef = useRef<HTMLDivElement>(null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (catRef.current && !catRef.current.contains(e.target as Node)) {
        setCatDropdownOpen(false);
      }
      if (langRef.current && !langRef.current.contains(e.target as Node)) {
        setLangDropdownOpen(false);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    function handleScroll() {
      setScrolled(window.scrollY > 20);
    }
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const navLinks = [
    { to: "/", label: "navHome" },
    { to: "/products", label: "navProducts" },
    { to: "/guides", label: "navGuides" },
    { to: "/shipping", label: "navShipping" },
    { to: "/reviews", label: "navReviews" },
    { to: "/safety", label: "navSafety" },
    { to: "/comparisons", label: "navComparisons" },
    { to: "/faq", label: "navFaq" },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? "bg-background-50/95 backdrop-blur-md shadow-sm border-b border-background-200"
          : "bg-transparent"
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <div className="w-8 h-8 md:w-10 md:h-10 rounded-lg bg-primary-500 flex items-center justify-center">
              <i className="ri-table-2 text-background-50 text-lg md:text-xl" />
            </div>
            <span className="font-heading font-bold text-lg md:text-xl text-foreground-900 tracking-tight">
              {t("siteName")}
            </span>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {/* Categories Dropdown */}
            <div className="relative" ref={catRef}>
              <button
                onClick={() => setCatDropdownOpen(!catDropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-200/70 transition-colors"
              >
                {t("navCategories")}
                <i className={`ri-arrow-down-s-line transition-transform ${catDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {catDropdownOpen && (
                <div className="absolute top-full left-0 mt-1 w-72 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden">
                  <div className="max-h-[400px] overflow-y-auto p-2">
                    {categories.map((cat) => (
                      <a
                        key={cat.id}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 transition-colors text-sm text-foreground-700 hover:text-foreground-900"
                      >
                        <span className="w-8 h-8 rounded-md bg-background-200/70 flex items-center justify-center shrink-0">
                          <i className="ri-shopping-bag-line text-foreground-600" />
                        </span>
                        {t(cat.nameKey)}
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-200/70 transition-colors"
              >
                {t(link.label)}
              </Link>
            ))}
          </div>

          {/* Right Side: Language + CTAs */}
          <div className="hidden lg:flex items-center gap-2">
            {/* Language Switcher */}
            <div className="relative" ref={langRef}>
              <button
                onClick={() => setLangDropdownOpen(!langDropdownOpen)}
                className="flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium text-foreground-700 hover:text-foreground-900 hover:bg-background-200/70 transition-colors"
              >
                <i className="ri-global-line" />
                {languages.find((l) => l.code === i18n.language)?.label
                  ? t(languages.find((l) => l.code === i18n.language)!.label)
                  : "EN"}
                <i className={`ri-arrow-down-s-line transition-transform ${langDropdownOpen ? "rotate-180" : ""}`} />
              </button>
              {langDropdownOpen && (
                <div className="absolute top-full right-0 mt-1 w-40 bg-background-50 rounded-xl border border-background-200/70 shadow-lg overflow-hidden p-1">
                  {languages.map((lang) => (
                    <button
                      key={lang.code}
                      onClick={() => {
                        i18n.changeLanguage(lang.code);
                        setLangDropdownOpen(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                        i18n.language === lang.code
                          ? "bg-primary-100 text-primary-700 font-medium"
                          : "text-foreground-700 hover:bg-background-100"
                      }`}
                    >
                      {t(lang.label)}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="px-4 py-2 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
            >
              {t("ctaBuyNow")}
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            className="lg:hidden p-2 rounded-lg text-foreground-700 hover:bg-background-200/70"
            onClick={() => setMenuOpen(!menuOpen)}
          >
            <i className={`ri-${menuOpen ? "close" : "menu"}-line text-xl`} />
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {menuOpen && (
        <div className="lg:hidden bg-background-50/95 backdrop-blur-md border-t border-background-200/70 max-h-[80vh] overflow-y-auto">
          <div className="px-4 py-4 space-y-1">
            <p className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              {t("navCategories")}
            </p>
            {categories.map((cat) => (
              <a
                key={cat.id}
                href={cat.url}
                target="_blank"
                rel="noopener nofollow"
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100 text-sm text-foreground-700"
                onClick={() => setMenuOpen(false)}
              >
                <i className="ri-shopping-bag-line text-foreground-400" />
                {t(cat.nameKey)}
              </a>
            ))}
            <div className="border-t border-background-200/70 my-2" />
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="block px-3 py-2.5 rounded-lg hover:bg-background-100 text-sm font-medium text-foreground-700"
                onClick={() => setMenuOpen(false)}
              >
                {t(link.label)}
              </Link>
            ))}
            <div className="border-t border-background-200/70 my-2" />
            <p className="px-3 py-2 text-xs font-semibold text-foreground-400 uppercase tracking-wider">
              Language
            </p>
            {languages.map((lang) => (
              <button
                key={lang.code}
                onClick={() => {
                  i18n.changeLanguage(lang.code);
                  setMenuOpen(false);
                }}
                className={`block w-full text-left px-3 py-2.5 rounded-lg text-sm ${
                  i18n.language === lang.code
                    ? "bg-primary-100 text-primary-700 font-medium"
                    : "text-foreground-700 hover:bg-background-100"
                }`}
              >
                {t(lang.label)}
              </button>
            ))}
            <div className="pt-2">
              <a
                href="https://kaspreadsheet.com"
                target="_blank"
                rel="noopener nofollow"
                className="block w-full text-center px-4 py-3 rounded-lg bg-primary-500 text-background-50 text-sm font-semibold"
              >
                {t("ctaBuyNow")}
              </a>
            </div>
          </div>
        </div>
      )}
    </nav>
  );
}